import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const useManagedPortalContainer = (host: Element | null, name: string) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!host) {
      setContainer(null);
      return;
    }

    const portalContainer = document.createElement('div');
    portalContainer.setAttribute('data-bc-portal', name);
    // Avoid changing host layout while still giving React a stable parent.
    portalContainer.style.display = 'contents';

    // The container is in the host only while it has content. Host themes hide an
    // empty slot with `:empty`, so a slot whose button renders nothing must stay empty.
    const syncAttached = () => {
      if (!host.isConnected) return;
      const hasContent = portalContainer.hasChildNodes();
      if (hasContent && portalContainer.parentNode !== host) {
        host.appendChild(portalContainer);
      } else if (!hasContent && portalContainer.parentNode) {
        portalContainer.parentNode.removeChild(portalContainer);
      }
    };

    setContainer(portalContainer);

    // React renders into the detached container. Attach it when content appears, detach it when content goes.
    const contentObserver = new MutationObserver(() => syncAttached());
    contentObserver.observe(portalContainer, { childList: true, subtree: false });

    // Knockout / Magento may clear host children on re-render.
    const hostObserver = new MutationObserver(() => syncAttached());
    hostObserver.observe(host, { childList: true, subtree: false });

    return () => {
      contentObserver.disconnect();
      hostObserver.disconnect();
      setContainer(null);
      if (portalContainer.parentNode) {
        portalContainer.parentNode.removeChild(portalContainer);
      }
    };
  }, [host, name]);

  return container;
};

export const ManagedPortal = ({ host, name, children }: { host: Element | null; name: string; children: ReactNode }) => {
  const container = useManagedPortalContainer(host, name);
  if (!container) return null;
  return createPortal(children, container);
};
